/**
 * Shop Hooks
 *
 * React Query hooks for shop category management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { shopApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type { CreateShopCategoryData, UpdateShopCategoryData } from '../types'

// =============================================================================
// Query Keys
// =============================================================================

export const shopKeys = {
  all: ['shop'] as const,
  categories: () => [...shopKeys.all, 'categories'] as const,
  categoryList: (filters: Record<string, unknown>) =>
    [...shopKeys.categories(), 'list', filters] as const,
  categoryDetail: (id: number) => [...shopKeys.categories(), 'detail', id] as const,
}

// =============================================================================
// Shop Category Hooks
// =============================================================================

export function useShopCategories(params?: { enabled?: boolean; featured?: boolean }) {
  return useQuery({
    queryKey: shopKeys.categoryList(params || {}),
    queryFn: () => shopApi.getCategories(params),
  })
}

export function useShopCategory(id: number) {
  return useQuery({
    queryKey: shopKeys.categoryDetail(id),
    queryFn: () => shopApi.getCategory(id),
    enabled: !!id,
  })
}

export function useCreateShopCategory() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateShopCategoryData) => shopApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shopKeys.categories() })
      toast({
        title: 'Category Created',
        description: 'Your shop category has been created.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create category',
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateShopCategory() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateShopCategoryData }) =>
      shopApi.updateCategory(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: shopKeys.categoryDetail(variables.id) })
      queryClient.invalidateQueries({ queryKey: shopKeys.categories() })
      toast({
        title: 'Category Updated',
        description: 'Your shop category has been updated.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update category',
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteShopCategory() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: number) => shopApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shopKeys.categories() })
      toast({
        title: 'Category Deleted',
        description: 'Your shop category has been removed.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete category',
        variant: 'destructive',
      })
    },
  })
}
